import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MdLockOutline, MdPermIdentity, MdCheckCircle } from "react-icons/md";
import Form from "../forms/Form";
import toast from "react-hot-toast";
import AuthTextBox from "./inputs/AuthTextBox";
import { UseFormSetError } from "react-hook-form";
import {
  verifyInvitation,
  completeInvitedUserRegistration
} from "../../utils/api/endpoints/user/invitation";

type RegisterFields = {
  username: string;
  password: string;
  confirmPassword: string;
  display_name?: string;
  country?: string;
};

const InvitedUserRegistrationForm = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [invitationData, setInvitationData] = useState<{
    email: string;
    role: number;
  } | null>(null);
  const [loading, setLoading] = useState(false); // Handle loading state

  useEffect(() => {
    const validateInvitation = async () => {
      if (token) {
        try {
          const result = await verifyInvitation(token);
          if (result.valid) {
            setInvitationData(result);
          } else {
            toast.error("Invalid or expired invitation");
            navigate("/invalid-invitation");
          }
        } catch (error) {
          toast.error("Something went wrong while verifying the invitation.");
          navigate("/invalid-invitation");
        }
      }
    };
    validateInvitation();
  }, [token, navigate]);

  const handleSubmit = async (
    data: RegisterFields,
    setError: UseFormSetError<RegisterFields>
  ) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: "Passwords do not match"
      });
      return;
    }

    if (token) {
      setLoading(true);
      try {
        const registerResult = await completeInvitedUserRegistration(token, {
          username: data.username,
          password: data.password,
          display_name: data.display_name,
          country: data.country
        });

        if (registerResult) {
          toast.success("Registration successful! Redirecting to login...");
          setTimeout(() => navigate("/login"), 2000);
        }
      } catch (error: any) {
        console.error("Registration failed:", error);

        if (error.response) {
          const errorMessage = error.response.data.message;

          // Handle specific errors from backend
          if (errorMessage.includes("Invitation has expired")) {
            toast.error("This invitation link has expired.");
            navigate("/invalid-invitation");
          } else if (errorMessage.includes("Username already taken")) {
            setError("username", { message: "Username is already taken" });
          } else if (errorMessage.includes("Password must have at least")) {
            setError("password", { message: errorMessage });
          } else {
            toast.error(
              errorMessage || "Registration failed. Please try again."
            );
          }
        } else {
          toast.error("Network error. Please check your connection.");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const defaultValues = useMemo(
    () => ({
      username: "",
      password: "",
      confirmPassword: "",
      display_name: "",
      country: ""
    }),
    []
  );

  if (!invitationData) {
    return <div>Loading...</div>;
  }

  return (
    <article className="mx-auto w-full max-w-3xl">
      {/* Header brand and content without card wrapper (align with login/register) */}
      {/** Top brand row temporarily hidden per UI request */}
      {/**
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-3 transition-opacity hover:opacity-80">
          <img src="/ctb-logo-crop.svg" alt="CTB Logo" className="h-12 w-auto" />
          <span className="text-2xl font-semibold text-gray-900">Capture The Bug</span>
        </Link>
      </div>
      */}

      <div className="">
        {/* Centered brand logo (replaces success check icon) */}
        <div className="mb-6 flex justify-center">
          <img src="/ctb-logo-crop.svg" alt="Capture The Bug" className="h-16 w-auto" />
        </div>

        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">
            Complete Your Registration
          </h1>
          <p className="text-sm text-gray-600">
            Welcome <span className="font-medium text-blue-600">{invitationData.email}</span>!
            Please complete your registration to get started.
          </p>
        </div>

        <Form
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Two-column layout; country stays last */}
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-10">
            <AuthTextBox
              label="Username"
              name="username"
              placeholder="Choose your username"
              rules={{ required: true }}
              Icon={MdPermIdentity}
            />

            <AuthTextBox
              label="Display Name (Optional)"
              name="display_name"
              placeholder="Enter your display name"
              Icon={MdPermIdentity}
            />

            <AuthTextBox
              label="Password"
              name="password"
              placeholder="Enter your password"
              password={true}
              rules={{
                required: true,
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                  message:
                    "Password must contain at least 8 characters, including uppercase, lowercase, number, and special character."
                }
              }}
              Icon={MdLockOutline}
            />

            <AuthTextBox
              label="Confirm Password"
              name="confirmPassword"
              placeholder="Confirm your password"
              password={true}
              rules={{ required: true }}
              Icon={MdLockOutline}
            />

            {/* Country as the last, full-width */}
            <div className="md:col-span-2">
              <AuthTextBox
                label="Country"
                name="country"
                placeholder="Enter your country"
                Icon={MdPermIdentity}
              />
            </div>
          </div>

          {/* Password Requirements */}
          <div className="rounded-md bg-blue-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-blue-900">Password Requirements:</h4>
            <ul className="space-y-1 text-xs text-blue-700">
              <li>• At least 8 characters long</li>
              <li>• Contains uppercase and lowercase letters</li>
              <li>• Contains at least one number</li>
              <li>• Contains at least one special character</li>
            </ul>
          </div>

          {/* Submit Button - Clean professional design */}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-[#010D2C] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#010D2C] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Processing..." : "Complete Registration"}
          </button>
        </Form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </article>
  );
};

export default InvitedUserRegistrationForm;
